import { Injectable } from '@angular/core';
import { FiCustomerDataService, IPKCustomerData } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { AccessRightType, ApplicationSettingsType, ObjectTypes } from '@fi-shared/types';
import { ILightWeightBusinessUnit } from '@fi-shared/models';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { IContactModel } from '@fi-shared/models/common';
import { IFiUniqueContactData } from '@fi-shared/fi-directives/fi-custom-validators/fi-unique-contact-async-validator.directive';
import { IUserContactUniqueData } from '@fi-shared/fi-auth-shared/fi-authenticate-shared.service';
import { FiContactsService, FiUserContactDataItemType } from '@fi-shared/fi-contacts/fi-contacts.service';
import { IFiContactData } from '@fi-shared/fi-contacts/fi-contacts';
import { Observable } from 'rxjs';
import { IUserModel } from '@fi-shared/models/user.model';

@Injectable()
export class FiUserHelperService {
    constructor(
        private customerDataService: FiCustomerDataService,
        private userDataService: FiUserDataService,
        private fiBackend: FiBackendService,
        private fiSignalRService: FiSignalRService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private fiContactsService: FiContactsService,
    ) {}

    init() {
        this.fiSignalRService.dbOperationEvent.subscribe((data) => {
            if (data.ObjectType === ObjectTypes.PKSettings || data.ObjectType === ObjectTypes.BusinessUnit) {
                const hasAccessToPKModule = this.accessToApplicationTypeService.HasAccessToApplicationType(
                    ApplicationSettingsType.Personalkollen,
                    AccessRightType.Read,
                );
                if (hasAccessToPKModule) {
                    this.fiBackend.fetchData<IPKCustomerData>('PK/PK/GetPKCustomerData').then((personalkollenData) => {
                        this.customerDataService.setPersonalkollenData(personalkollenData);
                    });
                }
            }
        });
    }

    isPKUser(user: { UserToBusinessUnitIds: number[] }): boolean {
        // https://chainformation.atlassian.net/browse/FI-5482
        // if current user has intersection with personalkollen ids then they treat as PK one
        // and some properties should be disabled for editing
        const pkUnitIds = this.customerDataService.customerData.PersonalkollenData.PKUnitIds;
        const usersPKUnitId = user.UserToBusinessUnitIds.find((buId) => pkUnitIds.indexOf(buId) !== -1);
        return !!usersPKUnitId;
    }

    public isBusinessUnitsDisabledByPKUnits(businessUnit: ILightWeightBusinessUnit) {
        return (
            businessUnit &&
            this.customerDataService.customerData.PersonalkollenData.PKUnitIds.indexOf(businessUnit.Id) !== -1
        );
    }

    isAddUserEnabled(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            if (!this.customerDataService.customerData.PersonalkollenData.PKUnitIds.length) {
                resolve(true);
            } else {
                this.hasNonPersonalkollenUnits().then(resolve, reject);
            }
        });
    }

    private hasNonPersonalkollenUnits(): Promise<boolean> {
        return this.fiBackend.fetchData<boolean>('PK/PK/HasNonPersonalkollenUnits');
    }

    getDataForUniqueEmailCheckFn(
        userId: number,
        isSystemObject: boolean,
        contacts: IContactModel[],
    ): (contact: IContactModel) => IFiUniqueContactData {
        return (emailAddress: IContactModel) => {
            return {
                userId: userId,
                doCheck: !isSystemObject,
                throwOnlyWhenContactUsedAsLogin: !emailAddress.IsSignIn,
                contacts: contacts,
                isSignIn: emailAddress.IsSignIn,
            };
        };
    }

    getUserContactData(args: {
        user: IUserModel;
        userContactDataItemType: FiUserContactDataItemType;
        contactUniqueDataFn: () => IUserContactUniqueData[];
        isPKUser: boolean;
        onDestroy$: Observable<void>;
    }): IFiContactData {
        return this.fiContactsService.getUserContactData({
            user: args.user,
            itemType: args.userContactDataItemType,
            isPKUser: args.isPKUser,
            contactUniqueData: args.contactUniqueDataFn(),
            onDestroy$: args.onDestroy$,
        });
    }
}
